import { toConsumableArray as _toConsumableArray, extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import _omit from 'lodash/omit';
import { TooltipPopper, Tooltip, Paragraph, Anchor } from '@zillow/constellation';
import { event } from '@zillow/universal-analytics';
import { yieldCallback } from '@zillow/yield-callback';
import { TextInline, usePageProps, getCmsInfoForPage } from '@zillow/zrs-cms';
import * as HtmlToReact from 'html-to-react';
import React from 'react';
import { withTrigger } from './helpers.js';
import { usePopulateParamPlaceHolders } from './populateParamPlaceholders.js';

var HtmlToReactParser = HtmlToReact.Parser;
var htmlToReactParser = new HtmlToReactParser();
var processNodeDefinitions = new HtmlToReact.ProcessNodeDefinitions(React);
function processingInstructions(_ref) {
  _ref._blockAttributes;
  return [
  // Spans
  {
    shouldProcessNode: function shouldProcessNode(node) {
      var _node$attribs;
      return node.name === 'span' && ((_node$attribs = node.attribs) === null || _node$attribs === void 0 ? void 0 : _node$attribs.color);
    },
    processNode: function processNode(node, children, nodeIndex) {
      var ProcessedSpan = function ProcessedSpan() {
        return /*#__PURE__*/React.createElement(TextInline, {
          key: nodeIndex + "-span-" + node.name,
          fontColor: node.attribs.color
        }, children);
      };
      return /*#__PURE__*/React.createElement(ProcessedSpan, null, " ", children);
    }
  },
  // Links
  {
    shouldProcessNode: function shouldProcessNode(node) {
      return node.name === 'a';
    },
    processNode: function processNode(node, children, nodeIndex) {
      var AnchorWithTrigger = withTrigger(Anchor);
      var CustomAnchor = function CustomAnchor() {
        var pageProps = usePageProps();
        var href = usePopulateParamPlaceHolders(node.attribs.href);
        return /*#__PURE__*/React.createElement(AnchorWithTrigger, _extends({
          triggerLink: node.attribs.href,
          key: nodeIndex + "-anchor-" + node.name
        }, _omit(node.attribs, 'style', 'class'), {
          href: href,
          onMouseEnter: function onMouseEnter(e) {
            event({
              // https://dataportal.zillowgroup.net/event-registry/2049
              envelope: {
                event_type_id: '2049',
                event_type_version_id: '1',
                event_template_id: '126',
                event_template_version_id: '1'
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: 'interaction',
                trigger_location_nm: 'cms',
                trigger_source_nm: 'cms_anchor',
                trigger_object_nm: 'no_trigger_object'
              },
              semantic: {
                semantic_event_nm: 'cms_hover'
              },
              cms_info: Object.assign({
                block_id: e.target.id || '',
                block_type_cd: 'anchor',
                block_displayed_txt: e.target.textContent,
                block_target_url: href,
                block_target_window_txt: node.attribs.target || ''
              }, getCmsInfoForPage(pageProps))
            });
          },
          onClick: yieldCallback(function (e) {
            var _node$attribs$onClick, _node$attribs2;
            (_node$attribs$onClick = (_node$attribs2 = node.attribs).onClick) === null || _node$attribs$onClick === void 0 || _node$attribs$onClick.call(_node$attribs2, e);
            event({
              // https://dataportal.zillowgroup.net/event-registry/2048
              envelope: {
                event_type_id: '2048',
                event_type_version_id: '1',
                event_template_id: '126',
                event_template_version_id: '1'
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: 'interaction',
                trigger_location_nm: 'cms',
                trigger_source_nm: 'cms_anchor',
                trigger_object_nm: 'no_trigger_object'
              },
              semantic: {
                semantic_event_nm: 'cms_click'
              },
              cms_info: Object.assign({
                block_id: e.target.id || '',
                block_type_cd: 'anchor',
                block_displayed_txt: e.target.textContent,
                block_target_url: node.attribs.href,
                block_target_window_txt: node.attribs.target || ''
              }, getCmsInfoForPage(pageProps))
            });
          })
        }), children);
      };
      return /*#__PURE__*/React.createElement(CustomAnchor, {
        key: nodeIndex + "-anchor-container-" + node.name
      });
    }
  },
  // Tooltips
  {
    shouldProcessNode: function shouldProcessNode(node) {
      var _node$attribs3;
      return ((_node$attribs3 = node.attribs) === null || _node$attribs3 === void 0 ? void 0 : _node$attribs3.class) === 'constellation-tooltip';
    },
    processNode: function processNode(node, children, nodeIndex) {
      var TooltipContainer = function TooltipContainer() {
        var pageProps = usePageProps();
        return /*#__PURE__*/React.createElement(TooltipPopper, {
          key: nodeIndex + "-tooltip-" + node.name,
          onOpen: function onOpen() {
            var _node$attribs4;
            event({
              // https://dataportal.zillowgroup.net/event-registry/5030
              envelope: {
                event_type_id: '5030',
                event_type_version_id: '1',
                event_template_id: '297',
                event_template_version_id: '1'
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: 'interaction',
                trigger_location_nm: 'cms',
                trigger_source_nm: 'cms_tooltip',
                trigger_object_nm: 'no_trigger_object'
              },
              semantic: {
                semantic_event_nm: 'cms_open'
              },
              cms_info: Object.assign({
                block_id: (node === null || node === void 0 || (_node$attribs4 = node.attribs) === null || _node$attribs4 === void 0 ? void 0 : _node$attribs4.id) || '',
                block_type_cd: 'tooltip',
                block_displayed_txt: children[0] || ''
              }, getCmsInfoForPage(pageProps))
            });
          },
          onClose: function onClose() {
            var _node$attribs5;
            event({
              // https://dataportal.zillowgroup.net/event-registry/2051
              envelope: {
                event_type_id: '2051',
                event_type_version_id: '1',
                event_template_id: '126',
                event_template_version_id: '1'
                // Master ID: 2284.2.126.1
              },
              clickstream_trigger: {
                trigger_type_nm: 'interaction',
                trigger_location_nm: 'cms',
                trigger_source_nm: 'cms_tooltip',
                trigger_object_nm: 'no_trigger_object'
              },
              semantic: {
                semantic_event_nm: 'cms_close'
              },
              cms_info: Object.assign({
                block_id: (node === null || node === void 0 || (_node$attribs5 = node.attribs) === null || _node$attribs5 === void 0 ? void 0 : _node$attribs5.id) || '',
                block_type_cd: 'tooltip',
                block_displayed_txt: children[0] || ''
              }, getCmsInfoForPage(pageProps))
            });
          },
          triggered: /*#__PURE__*/React.createElement(Tooltip, {
            body: /*#__PURE__*/React.createElement(Paragraph, null, children)
          })
        });
      };
      return /*#__PURE__*/React.createElement(TooltipContainer, null);
    }
  }, {
    // Anything else
    shouldProcessNode: function shouldProcessNode() {
      return true;
    },
    processNode: processNodeDefinitions.processDefaultNode
  }];
}

/**
 * @param {Object} content
 * @param {Object} [options]
 * @param {Object} [options.pageProps]
 * @param {Object} [options.extraInstructions]
 * @returns
 */
function makeContentInteractive(content, options) {
  if (options === void 0) {
    options = {};
  }
  return htmlToReactParser.parseWithInstructions(content, function () {
    return true;
  }, [].concat(_toConsumableArray(options.extraInstructions || []), _toConsumableArray(processingInstructions(Object.assign({}, options)))));
}

export { makeContentInteractive as default };
